import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Eye, Trash2, X } from 'lucide-react';
import { API_BASE_URL } from '../../config/environment';
import CreateEventForm from '../../components/admin/CreateEventForm';
import EditEventForm from '../../components/admin/EditEventForm';

export default function EventManagement() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [location, setLocation] = useState('all');
  const [visibility, setVisibility] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadEvents();
  }, [location, visibility]);

  const loadEvents = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/events?location=${location}&visibility=${visibility}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to load events');
      
      const data = await response.json();
      setEvents(data.events);
    } catch (error) {
      console.error('Failed to load events:', error);
      if (error.message.includes('401')) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (eventId) => {
    // TODO: Implement publish endpoint
    alert('Publish feature coming soon!');
  };

  const handleDelete = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"? This cannot be undone.`)) {
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete event');
      
      alert('Event deleted successfully');
      loadEvents();
    } catch (error) {
      alert('Failed to delete event: ' + error.message);
    }
  };

  const handleClone = async (eventId, eventTitle) => {
    if (!window.confirm(`Clone "${eventTitle}"? A draft copy will be created for you to edit.`)) {
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/events/${eventId}/clone`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to clone event');
      
      const result = await response.json();
      alert(`Event cloned! ${result.message}`);
      loadEvents();
    } catch (error) {
      alert('Failed to clone event: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1DFD7]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="text-[#4C5A70] hover:text-[#0B2240]"
              >
                ← Dashboard
              </button>
              <h1 className="font-heading text-2xl font-semibold text-[#0B2240]">Manage Events</h1>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#0B2240] text-white rounded-lg hover:bg-[#0A1D35]"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4" />
              Create Event
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl p-4 shadow-soft border border-[#E1DFD7] mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Location</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]"
              >
                <option value="all">All Locations</option>
                <option value="Charlotte">Charlotte</option>
                <option value="Mooresville">Mooresville</option>
                <option value="Winston-Salem">Winston-Salem</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Visibility</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
          </div>
        </div>

        {/* Events Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-[#4C5A70]">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-xl p-12 shadow-soft border border-[#E1DFD7] text-center">
            <p className="text-[#4C5A70] mb-4">No events found</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="text-[#1FA3A3] hover:underline"
            >
              Create your first event
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-soft border border-[#E1DFD7] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F7F6F2] border-b border-[#E1DFD7]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase tracking-wider">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase tracking-wider">RSVPs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1DFD7]">
                {events.map(event => (
                  <tr key={event.id} className="hover:bg-[#F7F6F2] transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-[#0B2240]">{event.title}</div>
                        <div className="text-sm text-[#4C5A70]">{event.type.replace('_', ' ')}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4C5A70]">
                      {new Date(event.start_datetime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4C5A70]">{event.location_city}</td>
                    <td className="px-6 py-4 text-sm text-[#4C5A70]">
                      {event.capacity ? (
                        <div>
                          <div className="font-medium">{event.total_rsvps}/{event.capacity}</div>
                          {event.blocked_seats > 0 && (
                            <div className="text-xs text-[#D4AF37] mt-0.5">
                              ({event.total_rsvps} real + {event.blocked_seats} 🔒)
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>{event.total_rsvps}/∞</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        event.visibility === 'published' ? 'bg-[#1FA3A3]/10 text-[#1FA3A3]' :
                        event.visibility === 'draft' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                        'bg-[#EDEBE6] text-[#4C5A70]'
                      }`}>
                        {event.visibility}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.open(`/events/${event.slug}`, '_blank')}
                          className="p-2 text-[#1FA3A3] hover:bg-[#1FA3A3]/10 rounded-lg transition-colors"
                          title="View on Public Site"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingEvent(event)}
                          className="p-2 text-[#0B2240] hover:bg-[#0B2240]/10 rounded-lg transition-colors"
                          title="Edit Event"
                          data-testid="edit-event-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleClone(event.id, event.title)}
                          className="px-2 py-1 text-xs bg-[#D4AF37] text-white rounded hover:bg-[#C7A52A] transition-colors"
                          title="Clone Event"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => navigate(`/admin/events/${event.id}/rsvps`)}
                          className="px-2 py-1 text-xs bg-[#1FA3A3] text-white rounded hover:bg-[#157A7A]"
                          title="View RSVPs"
                        >
                          RSVPs ({event.total_rsvps})
                        </button>
                        <button
                          onClick={() => handleDelete(event.id, event.title)}
                          className="p-2 text-[#C7362E] hover:bg-[#C7362E]/10 rounded-lg transition-colors"
                          title="Delete"
                          data-testid="delete-event-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Event Form */}
      {showCreateForm && (
        <CreateEventForm
          onSuccess={(result) => {
            setShowCreateForm(false);
            loadEvents();
            alert(`Event created: ${result.slug}`);
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}
      
      {/* Edit Event Form */}
      {editingEvent && (
        <EditEventForm
          event={editingEvent}
          onSuccess={(result) => {
            setEditingEvent(null);
            loadEvents();
            if (result.significant_changes) {
              alert(`Event updated! ${result.rsvps_notified} attendees will be notified.`);
            }
          }}
          onCancel={() => setEditingEvent(null)}
        />
      )}
    </div>
  );
}
